import type { Disagreement, EvidenceEffect, EvidenceItem, PhotoCheck, SearchHit, SearchVerdict, VerdictReason } from './types'

/**
 * Словарь кодов ответа поиска → слова для человека (RP-FB65/FB66). Сервер отдаёт только коды и числа, фраз он не собирает; неизвестный код
 * показывается как есть, а не придумывается.
 */

/** Вид свидетельства (`EvidenceItem.kind`) → название в списке «Из чего сложилось». */
const EVIDENCE_LABELS: Readonly<Record<string, string>> = {
  barcode: 'Штрихкод',
  text: 'Слова этикетки',
  numbers: 'Числа с этикетки',
  photo: 'Фото',
  judge: 'Проверка по фото',
}

export const evidenceLabel = (kind: string): string => EVIDENCE_LABELS[kind] ?? kind

/** Код пояснения свидетельства (`EvidenceItem.note`, `EvidenceNotes` сервера) → слова. */
const EVIDENCE_NOTES: Readonly<Record<string, string>> = {
  'barcode-verified': 'штрихкод совпал полностью',
  'barcode-unverified': 'штрихкод совпал не полностью',
  'text-strong': 'слова совпали хорошо',
  'text-weak': 'слова совпали слабо',
  'text-named-others': 'по словам этикетки найдены другие товары, этот — нет',
  'text-coverage-low': 'слова этикетки и названия сходятся плохо',
  'numbers-match': 'число из названия есть на этикетке',
  'numbers-mismatch': 'числа названия и этикетки не сошлись',
  'numbers-neutral': 'числа учтены, ничего не изменили',
  'photo-match': 'фото очень похоже на каталожное',
  'photo-similar': 'фото похоже',
  'photo-weak': 'фото похоже слабо',
  'photo-arbitrated': 'фото помогло различить варианты',
  'photo-undistinguishing': 'фото есть у всех вариантов, но выглядят они одинаково',
  'photo-missing-in-group': 'фото есть не у всех вариантов, между ними не сравнивали',
  'photo-not-compared': 'сходства по фото нет: фото в базе нет или оно сильно отличается',
  'judge-same': 'модель уверена: на снимке этот товар',
  'judge-different': 'модель считает: на снимке другой товар',
  'judge-unsure': 'модель не уверена',
  'judge-unavailable': 'проверка не выполнена: модель недоступна',
  'judge-timeout': 'проверка не успела',
}

/** Код «пояснять нечего»: свидетельства нет или оно очевидно из вида дорожки (`EvidenceNotes.None` сервера). */
const NOTE_NONE = 'none'

/** Пояснение к свидетельству словами; новый код сервера показывается как есть, код «пояснять нечего» — `null`. */
export const evidenceNote = (note: string): string | null => (note === NOTE_NONE ? null : (EVIDENCE_NOTES[note] ?? note))

/** Сторона проверки моделью по коду её ответа (RP-FB77 П4); сбой и таймаут — «нет данных». */
const JUDGE_EFFECTS: Readonly<Record<string, EvidenceEffect>> = { 'judge-same': 'for', 'judge-different': 'against', 'judge-unsure': 'neutral' }

/**
 * Влияние свидетельства: данных нет — `none` (это «нет данных», а не минус); данные есть и сдвинули в плюс — `for`, в минус — `against`,
 * не сдвинули (вклад 0, например, «фото не различает») — `neutral`.
 */
export function evidenceEffect(item: EvidenceItem): EvidenceEffect {
  // Проверка моделью числа не даёт (вклад 0): её сторона — в самом ответе. «Не уверена» — проверка была, но не сдвинула.
  if (item.kind === 'judge') return JUDGE_EFFECTS[item.note] ?? 'none'
  if (!item.present) return 'none'
  if (item.logLr > 0) return 'for'
  if (item.logLr < 0) return 'against'
  return 'neutral'
}

/** Свидетельство позиции по виду (`photo`, `text`…); нет — сервер прежний или дорожки в ответе нет. */
const evidenceOf = (hit: SearchHit, kind: string): EvidenceItem | undefined => hit.evidence?.find((item) => item.kind === kind)

/**
 * Что сказало фото о позиции: из `photoChecked` и кода свидетельства «фото». Ни поля, ни свидетельства — сервер прежний, подписи нет (`null`).
 * `photo-not-compared` и `photoChecked: false` — сверять было нечем (это не довод ни за, ни против).
 */
export function photoCheckOf(hit: SearchHit): PhotoCheck | null {
  const photo = evidenceOf(hit, 'photo')
  if (hit.photoChecked === undefined && photo === undefined) return null
  switch (photo?.note) {
    case 'photo-arbitrated':
      return 'arbitrated'
    case 'photo-undistinguishing':
      return 'indistinct'
    case 'photo-missing-in-group':
      return 'incomplete'
    default:
      return hit.photoChecked === true ? 'checked' : 'absent'
  }
}

/**
 * Чью сторону держит позиция в расхождении «текст ≠ фото»: сервер называет только факт (`disagrees`), сторона видна из свидетельств. Только
 * текст дал довод «за» — позицию назвал текст; только фото — фото; иначе сторона неизвестна и подпись общая. Не расходятся — `null`.
 */
export function disagreementOf(hit: SearchHit): Disagreement | null {
  if (hit.disagrees !== true) return null
  const supports = (kind: string): boolean => {
    const item = evidenceOf(hit, kind)
    return item !== undefined && item.present && item.logLr > 0
  }
  const byText = supports('text')
  const byPhoto = supports('photo')
  return byText && !byPhoto ? 'text' : byPhoto && !byText ? 'photo' : 'unknown'
}

/** Слова, которыми называется влияние: значок и слова, не только цвет. */
export const EVIDENCE_EFFECT_WORDS: Readonly<Record<EvidenceEffect, string>> = {
  for: 'за',
  against: 'против',
  neutral: 'не повлияло',
  none: 'нет данных',
}

/**
 * Слово у процента по вердикту сервера: `found` — «Скорее всего», `candidates` — «Возможно»; у остальных вердиктов слова нет. Пороги считает
 * сервер и сообщает их вердиктом: клиент своих порогов не заводит.
 */
export function confidenceWord(verdict: SearchVerdict): string | null {
  return verdict === 'found' ? 'Скорее всего' : verdict === 'candidates' ? 'Возможно' : null
}

/** Подпись позиции в расхождении «текст ≠ фото»: кто её назвал. */
export const DISAGREEMENT_LABELS: Readonly<Record<Disagreement, string>> = {
  text: 'Текст и фото расходятся — этот товар назвал текст этикетки',
  photo: 'Текст и фото расходятся — этот товар назвало фото',
  unknown: 'Текст и фото расходятся',
}

/**
 * Предупреждение под баннером вердикта по коду причины сервера. Известны только причины, которые меняют то, что делает человек; остальные
 * (`candidates`, `confident`…) отдельной фразы не требуют — `null`.
 */
export function reasonNote(reason: VerdictReason): string | null {
  switch (reason.code) {
    case 'text-photo-disagree':
      return 'Текст этикетки и фото указывают на разные товары. Проверьте, какой из них ваш.'
    case 'text-only':
      return 'Товар найден только по тексту этикетки: штрихкода и подтверждения по фото нет.'
    case 'similar-variants': {
      const differences = reason.params['differences']
      return differences === undefined || differences.trim() === ''
        ? 'Есть очень похожие варианты.'
        : `Есть очень похожие варианты, они отличаются: ${differences}.`
    }
    case 'judge-confirmed':
      return 'Проверка по фото выбрала этот товар среди похожих вариантов.'
    case 'judge-lead-different':
      return 'Проверка по фото считает, что первый вариант — другой товар. Сверьте внимательно.'
    case 'judge-unavailable':
      return 'Дополнительная проверка по фото не выполнена: модель недоступна. Выберите вариант сами.'
    case 'judge-timeout':
      return 'Дополнительная проверка по фото не успела. Выберите вариант сами.'
    default:
      return null
  }
}

/** Пометка у строки, прочитанной прицельным проходом (крупная область перечитана с увеличением, RP-FB65). Обычное чтение пометки не имеет. */
export const FOCUS_READ_LABEL = 'прицельно'
